/**
 * RFC 9457 Problem Details for HTTP APIs.
 * @see https://www.rfc-editor.org/rfc/rfc9457.html
 */
export interface ProblemDetail {
  type?: string;
  title?: string;
  status: number;
  detail?: string;
  instance?: string;
  /** Stable code for client-side message mapping (e.g. "conversation-create-forbidden"). */
  code?: string;
  /** Learning-synthesis guard that blocked the mutation (409 synthesis-guard-blocked). */
  guardId?: string;
  /** Workflow stage context for synthesis guards (e.g. INSIGHTS, TRACKER, LEARNING_GATE). */
  snapshotStage?: string;
  /** Tool-card catalog kind when code is tool-card-validation. */
  kind?: string;
  /** Tool-card schema phase (PAYLOAD | RESOLVE) when code is tool-card-validation. */
  phase?: string;
  validationMessages?: string[];
  [key: string]: unknown;
}

const PROBLEM_JSON = 'application/problem+json';

/**
 * Attempts to parse the response body as RFC 9457 Problem Detail.
 * Returns null if the response is not application/problem+json or application/json,
 * or if the body does not have a numeric status.
 */
export async function parseProblemDetail(
  response: Response
): Promise<ProblemDetail | null> {
  const contentType = response.headers.get('content-type') ?? '';
  if (
    !contentType.includes(PROBLEM_JSON) &&
    !contentType.includes('application/json')
  ) {
    return null;
  }
  try {
    const body = await response.json();
    if (body != null && typeof body.status === 'number') {
      return body as ProblemDetail;
    }
  } catch {
    // ignore
  }
  return null;
}

/** Error type that may carry RFC 9457 Problem Detail when thrown from API helpers */
export interface ApiErrorLike extends Error {
  problemDetail?: ProblemDetail;
}

/** Stable backend contract for a field-edit proposal whose recorded basis changed. */
export const FIELD_EDIT_PROPOSAL_STALE_CODE = "field-edit-proposal-stale";

/**
 * Single source for backend problem "code": user-facing message and whether it triggers full-page AccessDenied.
 * viewForbidden: true = show AccessDenied view; omit or false = keep toast/inline behavior.
 */
export const PROBLEM_CODE_ENTRIES: Record<
  string,
  { message: string; viewForbidden?: boolean }
> = {
  "access-denied": {
    message:
      "You don't have permission to perform this action. Ask the project or workspace owner to change your role.",
    viewForbidden: true,
  },
  "conversation-create-forbidden":
    { message: "You don't have permission to create conversations in this project." },
  "conversation-delete-forbidden":
    { message: "You don't have permission to delete this conversation." },
  "conversation-chat-forbidden":
    { message: "You don't have permission to send messages in this conversation." },
  "conversation-view-forbidden": {
    message: "You don't have permission to view this conversation.",
    viewForbidden: true,
  },
  "conversations-recent-forbidden": {
    message: "You don't have permission to view recent conversations.",
    viewForbidden: true,
  },
  "project-conversations-list-forbidden": {
    message: "You don't have permission to list conversations in this project.",
    viewForbidden: true,
  },
  "project-create-forbidden":
    { message: "You don't have permission to create projects in this workspace." },
  "projects-list-forbidden": {
    message: "You don't have permission to list projects.",
    viewForbidden: true,
  },
  "project-update-forbidden":
    { message: "You don't have permission to update this project." },
  "project-delete-forbidden":
    { message: "You don't have permission to delete this project." },
  "document-upload-forbidden":
    { message: "You don't have permission to upload documents to this project." },
  "document-list-forbidden": {
    message: "You don't have permission to list documents in this project.",
    viewForbidden: true,
  },
  "document-delete-forbidden":
    { message: "You don't have permission to delete this document." },
  "document-download-forbidden": {
    message: "You don't have permission to download this document.",
    viewForbidden: true,
  },
  "document-presigned-upload-forbidden":
    { message: "You don't have permission to upload documents to this project." },
  "document-access-forbidden": {
    message: "You don't have permission to access this document.",
    viewForbidden: true,
  },
  "share-forbidden":
    { message: "You don't have permission to share this resource." },
  "project-share-forbidden":
    { message: "You don't have permission to share this project." },
  "workspace-share-forbidden":
    { message: "You don't have permission to invite members to this workspace." },
  "conversation-share-forbidden":
    { message: "You don't have permission to share this conversation." },
  "experiment-view-forbidden": {
    message: "You don't have permission to view experiments in this project.",
    viewForbidden: true,
  },
  "experiment-update-forbidden":
    { message: "You don't have permission to update this experiment." },
  "experiment-delete-forbidden":
    { message: "You don't have permission to delete this experiment." },
  "assumption-field-edit-sealed": {
    message:
      "Assumptions are locked for this experiment path. Design a new experiment, start a new path, or revise the innovation path.",
  },
  "assumption-field-edit-path-change": {
    message:
      "Assumption field edit is not allowed for this path. Start a new path or revise the innovation path instead.",
  },
  "learning-view-forbidden": {
    message: "You don't have permission to view learnings in this project.",
    viewForbidden: true,
  },
  "learning-update-forbidden":
    { message: "You don't have permission to update this learning." },
  "learning-delete-forbidden":
    { message: "You don't have permission to delete this learning." },
  "credits-exhausted":
    { message: "You are out of innovation credits. Upgrade or top up to continue." },
  "plan-required":
    { message: "Select a plan to start chatting in this workspace." },
  "billing-profile-not-found":
    { message: "Select a plan to start chatting in this workspace." },
  "unlimited-plan-forbidden":
    { message: "Only Swifttracks users or admins can assign unlimited plans." },
  "insight-view-forbidden": {
    message: "You don't have permission to view insights in this project.",
    viewForbidden: true,
  },
  "insight-update-forbidden":
    { message: "You don't have permission to update this insight." },
  "insight-delete-forbidden":
    { message: "You don't have permission to delete this insight." },
  "waitlist-forbidden": {
    message: "You don't have permission to perform this action.",
    viewForbidden: true,
  },
  "payment-provider-unavailable": {
    message:
      "Payments are temporarily unavailable. Please try again in a moment.",
  },
  "checkout-session-creation-failed": {
    message: "We couldn't start the checkout session. Please try again.",
  },
  "customer-portal-unavailable": {
    message:
      "The subscription management portal is temporarily unavailable. Please try again shortly.",
  },
  "subscription-not-found": {
    message: "We couldn't find an active subscription for this workspace.",
  },
  "plan-not-purchasable": {
    message:
      "This plan can't be purchased through checkout. Pick a different plan or contact sales.",
  },
  "synthesis-guard-blocked": {
    message:
      "This action is not available at the current experiment synthesis stage.",
  },
  "tool-card-validation": {
    message: "Tool card data is invalid. Check the fields and try again.",
  },
  "tool-card-resolve-blocked": {
    message: "This action is no longer available for the current workflow stage.",
  },
  "action-request-stale": {
    message:
      "The facts behind this proposal changed. Review the refreshed proposal before continuing.",
  },
  [FIELD_EDIT_PROPOSAL_STALE_CODE]: {
    message:
      "The values behind this proposal changed. Ask the agent for a fresh proposal before applying it.",
  },
  MCP_AUTH_REQUIRED: {
    message: "Reconnect the connected app to continue.",
  },
  SOURCE_SEARCH_UNAVAILABLE: {
    message:
      "Atlassian cross-product search is unavailable for this connection. You can still browse projects and spaces.",
  },
  "invalid-credit-mutation": {
    message: "This credit change is not allowed for the workspace balance.",
  },
};

/** Derived: code → message for getErrorMessage and other callers. */
export const ERROR_CODE_MESSAGES: Record<string, string> = Object.fromEntries(
  Object.entries(PROBLEM_CODE_ENTRIES).map(([code, entry]) => [code, entry.message])
);

/** Derived: codes that trigger full-page AccessDenied. */
export const VIEW_FORBIDDEN_CODES: ReadonlySet<string> = new Set(
  Object.entries(PROBLEM_CODE_ENTRIES)
    .filter(([, entry]) => entry.viewForbidden === true)
    .map(([code]) => code)
);

export function isViewForbiddenCode(code: string | undefined): boolean {
  return typeof code === "string" && VIEW_FORBIDDEN_CODES.has(code);
}

/**
 * Parses the response as Problem Detail (if applicable), then throws an error
 * with message from detail/title or defaultMessage. Use when handling !response.ok.
 * When status is 403 and code is view-forbidden, sets global access-denied state
 * so the layout can show AccessDenied; then rethrows.
 */
export async function throwOnProblemResponse(
  response: Response,
  defaultMessage: string
): Promise<never> {
  const problem = await parseProblemDetail(response);
  if (response.status === 403 && problem && isViewForbiddenCode(problem.code)) {
    const pathname =
      typeof window !== "undefined" ? window.location.pathname : undefined;
    const { useAccessDeniedStore } = await import("@/stores/useAccessDeniedStore");
    useAccessDeniedStore.getState().setAccessDenied(
      {
        code: problem.code,
        status: problem.status,
        detail: problem.detail,
        title: problem.title,
        instance: problem.instance,
      },
      pathname
    );
  }
  const message = problem?.detail ?? problem?.title ?? defaultMessage;
  const error: ApiErrorLike = new Error(message);
  if (problem) {
    error.problemDetail = problem;
  }
  throw error;
}

/** Returns the problem detail code from an error if present. */
export function getProblemDetailCode(error: unknown): string | undefined {
  if (error && typeof error === "object" && "problemDetail" in error) {
    const p = (error as ApiErrorLike).problemDetail;
    return p && typeof p === "object" && "code" in p ? (p.code as string) : undefined;
  }
  return undefined;
}

/** Returns RFC 9457 problem detail attached to an API error, if any. */
export function getProblemDetail(error: unknown): ProblemDetail | undefined {
  if (error && typeof error === "object" && "problemDetail" in error) {
    const p = (error as ApiErrorLike).problemDetail;
    return p && typeof p === "object" ? p : undefined;
  }
  return undefined;
}

const SYNTHESIS_GUARD_STAGE_HINTS: Record<string, string> = {
  INSIGHTS: "Complete insight work in chat or the insights area first.",
  TRACKER: "Lock the experiment data tracker before continuing.",
  LEARNING_GATE: "Complete the learning gate before creating a learning.",
};

/** Optional UX hint for synthesis-guard-blocked snapshotStage values from the backend. */
export function getSynthesisGuardStageHint(snapshotStage: unknown): string | null {
  if (typeof snapshotStage !== "string" || snapshotStage.length === 0) {
    return null;
  }
  return SYNTHESIS_GUARD_STAGE_HINTS[snapshotStage] ?? null;
}

/**
 * User-facing message for synthesis-guard-blocked (409). Prefers backend detail over the generic
 * code message, then appends an optional stage hint when snapshotStage is present.
 */
export function getGuardBlockedDisplayMessage(error: unknown, fallback: string): string {
  const problem = getProblemDetail(error);
  if (problem?.code !== "synthesis-guard-blocked") {
    return getErrorMessage(error, fallback);
  }
  const primary =
    (typeof problem.detail === "string" && problem.detail.trim().length > 0
      ? problem.detail
      : null) ??
    ERROR_CODE_MESSAGES["synthesis-guard-blocked"] ??
    fallback;
  const hint = getSynthesisGuardStageHint(problem.snapshotStage);
  return hint ? `${primary} ${hint}` : primary;
}

/**
 * Returns a user-facing error message. When the error has problemDetail.code and it exists
 * in ERROR_CODE_MESSAGES, that message is returned; otherwise problemDetail.detail/title,
 * then message, then fallback.
 */
export function getErrorMessage(error: unknown, fallback: string): string {
  if (error && typeof error === "object") {
    const e = error as ApiErrorLike;
    const code = e.problemDetail?.code;
    if (typeof code === "string" && code in ERROR_CODE_MESSAGES) {
      return ERROR_CODE_MESSAGES[code];
    }
    return e.problemDetail?.detail ?? e.problemDetail?.title ?? e.message ?? fallback;
  }
  return error instanceof Error ? error.message : fallback;
}
